// hooks/use-chat-thread.js — single source of truth for chat thread mechanics.
//
// What this hook owns (locked cores — never reimplement in a feature):
//   - Scroll engine (pin user message to top, hold during streaming)
//   - Scroll lock (manual scroll-up detection)
//   - Show/hide scroll-to-bottom button + correct floor calculation
//   - SSE reader loop (data.t token accumulation, data.done finalization)
//   - AbortController + abort()
//   - sendingRef gate (prevents double-sends)
//   - loading state
//   - Two-step setMessages timing (user message → file upload → streaming placeholder)
//   - Streaming placeholder management (push, accumulate, finalize)
//   - 18mb body size guard
//   - AbortError handling (filter out streaming bubble)
//   - Other-error handling (replace streaming bubble with error text)
//   - Input refocus after send completes
//
// What this hook does NOT own (feature stays in control):
//   - Input state (contentEditable + slash picker quirks per feature)
//   - Messages state (controlled — feature owns it so polling/realtime can write)
//   - Initial message loading (every feature loads differently)
//   - Pending file state (feature manages, passes via beforeSend)
//   - Voice integration (useVoice exists separately, feature wires it)
//   - The buildRequest body shape (the only feature-specific surface)
//   - Post-response side effects (artifact detect, desc update, thread reload)
//   - System prompt building (handler's job, not the hook's)
//
// Usage shape:
//   const chat = useChatThread({
//     messages, setMessages,
//     buildRequest: async ({ text, fileData, messagesSnapshot, signal, isDeepSearch, ...extra }) => ({
//       url: '/api/chat',
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
//       body: { ... }, // object, gets JSON.stringify'd
//     }),
//     beforeSend: async () => ({ fileData: { ... } }),
//     onResponseDone: (finalText, sentMessage, metadata) => { /* metadata.assistantMsgId, metadata.chips, ... */ },
//   });
//
//   // Then in render:
//   <div ref={chat.scrollContainerRef}>
//     {messages.map((m, i) => <MessageRow ref={i === lastUserIdx ? chat.lastUserMsgRef : null} ... />)}
//   </div>
//   {chat.showScrollBtn && <button onClick={chat.scrollToBottom}>↓</button>}
//   <input ref={chat.inputRef} onKeyDown={e => { if (e.key === 'Enter') chat.send(input); }} />
//   {chat.loading ? <button onClick={chat.abort}>stop</button> : <button onClick={() => chat.send(input)}>send</button>}

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { generateId } from '../lib/render-helpers';

const DEFAULT_PIN_OFFSET = 40;
const DEFAULT_SCROLL_BTN_THRESHOLD = 80;
const DEFAULT_BODY_SIZE_LIMIT = 18000000; // 18mb — leaves headroom under Vercel's 20mb body parser
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Try again.';

export function useChatThread({
  // Required
  messages,
  setMessages,
  buildRequest,

  // Optional callbacks
  beforeSend,
  onResponseDone,
  onTextToken,
  onAbort,
  onError,

  // Optional config
  pinOffset = DEFAULT_PIN_OFFSET,
  scrollBtnThreshold = DEFAULT_SCROLL_BTN_THRESHOLD,
  bodySizeLimit = DEFAULT_BODY_SIZE_LIMIT,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  // Optional: caller-provided inputRef. If a feature already owns the
  // contentEditable ref (e.g. space passes it down from a parent that
  // also uses it for voice integration), pass it here so the hook
  // uses the same DOM element for autofocus instead of creating its own.
  inputRef: providedInputRef,
  // Optional: client-side typing animation. When true, SSE/JSON tokens
  // go into a buffer and are revealed at ~60 chars/second via rAF.
  // Adaptive catch-up: rate accelerates when buffer grows past 200 chars.
  // Default false — features keep their existing direct-render behavior
  // unless they opt in. See space chat for the reference usage.
  typingAnimation = false,
}) {
  // ── State ────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // ── Refs ─────────────────────────────────────────────────
  const sendingRef         = useRef(false);
  const abortControllerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pinnedScrollRef    = useRef(0);
  const lastScrollTopRef   = useRef(0);
  const isLockedRef        = useRef(false);
  const internalInputRef   = useRef(null);
  // Use the caller-provided inputRef when available (so the hook and the
  // feature share the same contentEditable DOM element), otherwise fall
  // back to the internally-owned one.
  const inputRef           = providedInputRef || internalInputRef;
  const lastUserMsgRef     = useRef(null);
  // Dynamic bottom spacer — feature attaches a <div ref={spacerRef} />
  // at the end of its messages list. The scroll engine sizes it
  // imperatively to allow pinning the last user message at top without
  // creating ghost space.
  const spacerRef          = useRef(null);
  const spacerHeightRef    = useRef(0);
  // Typing animation state — only populated when typingAnimation is true.
  // Holds the pending buffer, the shown text, animation frame id, and
  // a Promise that resolves when the animation finishes draining.
  // See the SEND function and abort() for usage.
  const streamStateRef     = useRef(null);

  // ── SCROLL ENGINE: dynamic bottom spacer + two-mode pin/follow ──
  //
  // The canonical Claude.ai/ChatGPT scroll behavior has two phases:
  //   Phase 1 (Pin): user message at top of viewport, AI grows DOWN
  //                  below it into the viewport
  //   Phase 2 (Follow): AI has filled the viewport zone below the
  //                     user message, so the user message gets pushed
  //                     UP out of view, and scroll auto-follows the
  //                     latest token at the bottom of the viewport
  //
  // The mechanism is a dynamic spacer div (owned by the feature, sized
  // by the hook) that fills just enough space to allow scrollTop to
  // pin the last user message. As AI content grows below the user
  // message, the spacer shrinks at the same rate — scrollHeight stays
  // constant, no visual jumps. When AI content exceeds the viewport
  // zone, the spacer hits 0 and we switch to follow mode.
  //
  // After streaming:
  //   - Short AI: max scrollTop = pin position. Can't scroll past
  //     user-msg-at-top. No ghost space below short AI.
  //   - Long AI: max scrollTop = bottom of content. Can't scroll past
  //     last token. No ghost space.
  //
  // Requires the feature to render <div ref={spacerRef} /> at the end
  // of its messages list. If spacerRef is not attached (feature hasn't
  // been updated yet), falls back to the old pin-via-scrollTop only.
  useLayoutEffect(() => {
    if (messages.length === 1 && inputRef.current) {
      inputRef.current.focus();
    }
    if (!messages.length) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    // Find the most recent user message via data attribute
    const userEls = container.querySelectorAll('[data-msg-role="user"]');
    const lastUserEl = userEls[userEls.length - 1];

    // No user message yet (e.g., only assistant content) — nothing to pin
    if (!lastUserEl) return;

    const spacer = spacerRef.current;

    // Legacy fallback: feature hasn't attached a spacer. Use the old
    // pin-via-scrollTop approach (still better than nothing, relies on
    // the feature's 100vh bottom padding to work).
    if (!spacer) {
      const lastMsg = messages[messages.length - 1];
      const prevMsg = messages.length >= 2 ? messages[messages.length - 2] : null;
      const justSent = lastMsg.role === 'user' || (lastMsg.streaming && prevMsg?.role === 'user');
      if (justSent && lastUserMsgRef.current) {
        const top = lastUserMsgRef.current.offsetTop - pinOffset;
        container.scrollTop = Math.max(0, top);
        pinnedScrollRef.current = container.scrollTop;
        return;
      }
      if (lastMsg.streaming) {
        container.scrollTop = pinnedScrollRef.current;
      }
      return;
    }

    // New mode: dynamic spacer + two-mode scroll
    const clientHeight = container.clientHeight;
    const userMsgOffsetTop = lastUserEl.offsetTop;
    const userMsgHeight = lastUserEl.offsetHeight;
    const userMsgBottom = userMsgOffsetTop + userMsgHeight;

    // Read actual spacer height from the DOM (not a cached ref) so we never
    // go out of sync with what the browser actually laid out. parseInt of
    // '510px' is fine; empty style.height is falsy, so we fall back to 0.
    const currentSpacerHeight = parseInt(spacer.style.height, 10) || 0;

    // Real content height = current scrollHeight minus the current spacer
    const realContentHeight = container.scrollHeight - currentSpacerHeight;
    const aiRegionHeight = realContentHeight - userMsgBottom;

    // The space below the pinned user msg that the viewport has to offer
    const neededSpaceBelow = clientHeight - pinOffset - userMsgHeight;

    if (aiRegionHeight < neededSpaceBelow) {
      // Phase 1: Pin mode — short AI or early in stream
      const newSpacerHeight = Math.max(0, neededSpaceBelow - aiRegionHeight);
      spacer.style.height = newSpacerHeight + 'px';
      spacerHeightRef.current = newSpacerHeight;
      // Force a synchronous layout flush so container.scrollTop's clamping
      // sees the new spacer height. Without this, setting scrollTop before
      // the browser has processed the style change can clamp to the old
      // (smaller) max and the pin appears to lag.
      void container.offsetHeight;
      if (!isLockedRef.current) {
        container.scrollTop = Math.max(0, userMsgOffsetTop - pinOffset);
        pinnedScrollRef.current = container.scrollTop;
      }
    } else {
      // Phase 2: Follow mode — AI has overflowed the viewport zone
      spacer.style.height = '0px';
      spacerHeightRef.current = 0;
      void container.offsetHeight;
      if (!isLockedRef.current) {
        container.scrollTop = container.scrollHeight - clientHeight;
        pinnedScrollRef.current = container.scrollTop;
      }
    }
  }, [messages, pinOffset]);

  // ── SCROLL LOCK ──────────────────────────────────────────
  // Detects when user manually scrolls up — disables auto-scroll lock.
  // Also drives the show/hide of the scroll-to-bottom button.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    function onScroll() {
      const diff = container.scrollTop - lastScrollTopRef.current;
      lastScrollTopRef.current = container.scrollTop;
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < scrollBtnThreshold;
      setShowScrollBtn(!atBottom && messages.length > 0);
      if (diff < -10 && loading) isLockedRef.current = true;
      if (atBottom) isLockedRef.current = false;
    }
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [loading, messages.length, scrollBtnThreshold]);

  // ── SCROLL TO BOTTOM ─────────────────────────────────────
  // max scrollTop works for both modes in the new scroll engine:
  //   Pin mode: max scrollTop = pin position (user msg at top, spacer filling below)
  //   Follow mode: max scrollTop = bottom of real content (last token at bottom)
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight - container.clientHeight;
    pinnedScrollRef.current = container.scrollTop;
    isLockedRef.current = false;
    setShowScrollBtn(false);
  }, []);

  // ── ABORT ────────────────────────────────────────────────
  // Cancels the in-flight fetch (if any) and flushes the typing animation
  // buffer (if any) so the user sees everything received so far and the
  // message finalizes immediately.
  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    const stream = streamStateRef.current;
    if (stream) {
      stream.flushed = true;
    }
  }, []);

  // ── SEND ─────────────────────────────────────────────────
  // The full send pipeline:
  //   1. Validate (text or file required, not already sending)
  //   2. Run beforeSend (file upload, return fileData)
  //   3. Push user message (setMessages)
  //   4. Push streaming placeholder (setMessages)
  //   5. buildRequest → fetch with abort signal
  //   6. SSE loop → accumulate tokens, finalize on done
  //   7. Handle errors (abort vs other) and clean up
  //
  // Returns: undefined (fire-and-forget). Side effects via setMessages + callbacks.
  const send = useCallback(async (rawText = '', extraOptions = {}) => {
    const text = (rawText || '').trim();

    // Run beforeSend FIRST so the feature can decide whether there's a file
    // (we need to know before validating "must have text or file").
    // Extras passed through from the call site (e.g. isAskShift, isDeepSearch
    // from space) so beforeSend can read them and decide its behavior.
    // text is also passed so beforeSend can do save-side-effects that need
    // the message content (e.g. space chat saving via /api/space-session).
    let beforeSendResult = {};
    if (beforeSend) {
      try {
        beforeSendResult = (await beforeSend({ text, ...extraOptions })) || {};
      } catch (e) {
        console.error('useChatThread beforeSend failed:', e);
        onError?.(e);
        return;
      }
    }
    const fileData = beforeSendResult.fileData || extraOptions.fileData || null;

    // Validation
    if (!text && !fileData) return;
    if (loading || sendingRef.current) return;

    sendingRef.current = true;
    setLoading(true);

    // ── Step 1: push user message ──────────────────────────
    // Two separate setMessages calls — critical for scroll engine timing.
    // First commit lets useLayoutEffect see the user message and pin.
    // Second commit (streaming placeholder) keeps the pin via the prevMsg check.
    const userMsg = {
      id: generateId(),
      role: 'user',
      content: text,
      ...(fileData?.isImage ? { isImage: true, imageData: fileData.base64, mimeType: fileData.type, imageName: fileData.name } : {}),
      ...(fileData?.isPDF  ? { isPdf:   true, pdfName: fileData.name } : {}),
      ...(beforeSendResult.userMsgExtras || {}),
    };
    setMessages(prev => [...prev, userMsg]);

    // ── Skip-response gate ─────────────────────────────────
    // beforeSend can return { skipResponse: true } to push the user message
    // but bypass the streaming placeholder, fetch, and SSE loop entirely.
    // Used by space chat in observer mode where the message saves but no
    // AI response is requested.
    if (beforeSendResult.skipResponse) {
      setLoading(false);
      sendingRef.current = false;
      setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
      return;
    }

    // ── Step 2: push streaming placeholder ─────────────────
    // streamId starts as client-generated; the JSON branch may rename it
    // to the server's saved-message id (if returned) so polling-based
    // realtime layers don't end up with duplicate copies of the same
    // message after the animation finalizes.
    let streamId = generateId();
    setMessages(prev => [...prev, {
      id: streamId,
      role: 'assistant',
      content: '',
      streaming: true,
    }]);

    // ── Step 2b: initialize typing animation state (if enabled) ──
    // Sets up a stream state object held in streamStateRef. The SSE/JSON
    // branches below push tokens into stream.pending instead of calling
    // setMessages directly. drainTick (a rAF loop) reveals chars at ~60
    // cps with adaptive catch-up. The animation owns finalization: it
    // sets streaming:false on the message AND fires onResponseDone when
    // the buffer drains. send() awaits stream.completePromise so loading
    // stays true until the reveal completes.
    if (typingAnimation) {
      const stream = {
        streamId,
        userMsg,
        pending: '',
        shownText: '',
        finalText: '',
        doneFired: false,
        flushed: false,    // set by abort() — drain immediately on next tick
        cancelled: false,  // set by error/cleanup — stop without finalizing
        lastTickTime: 0,
        frameId: null,
        completeResolve: null,
      };
      stream.completePromise = new Promise((resolve) => {
        stream.completeResolve = resolve;
      });
      streamStateRef.current = stream;
    }

    // The animation tick — drains stream.pending into stream.shownText
    // at the configured rate, with adaptive catch-up when the buffer
    // grows. When buffer empties AND doneFired (or flushed), finalizes
    // the message and resolves completePromise.
    function drainTick() {
      const stream = streamStateRef.current;
      if (!stream || stream.cancelled) return;

      const now = performance.now();
      const elapsed = now - stream.lastTickTime;
      stream.lastTickTime = now;

      // Compute reveal rate: 60 cps base, scales up to ~260 cps for big buffers
      let rate = 60;
      const bufLen = stream.pending.length;
      if (bufLen > 200) rate = 60 + Math.min((bufLen - 200) * 0.5, 200);
      const charsToReveal = Math.max(1, Math.floor(rate * elapsed / 1000));

      // Flushed (abort during reveal) — show everything immediately
      if (stream.flushed) {
        stream.shownText += stream.pending;
        stream.pending = '';
      } else if (stream.pending.length > 0) {
        const revealed = stream.pending.slice(0, charsToReveal);
        stream.pending = stream.pending.slice(charsToReveal);
        stream.shownText += revealed;
      }

      // Update visible message
      if (stream.shownText) {
        setMessages(prev => prev.map(m => m.id === stream.streamId
          ? { ...m, content: stream.shownText }
          : m));
      }

      // Termination: buffer empty AND done event fired (or flushed)
      if (stream.pending.length === 0 && (stream.doneFired || stream.flushed)) {
        const finalText = stream.finalText || stream.shownText;
        setMessages(prev => prev.map(m => m.id === stream.streamId
          ? { ...m, content: finalText, streaming: false }
          : m));
        try { onResponseDone?.(finalText, stream.userMsg, { ...(stream.doneMetadata || {}), assistantMsgId: stream.streamId }); } catch (e) { console.error('onResponseDone threw:', e); }
        streamStateRef.current = null;
        stream.completeResolve();
        return;
      }

      // Continue
      stream.frameId = requestAnimationFrame(drainTick);
    }

    // ── Step 3: build request and fetch ────────────────────
    let response;
    // responseType captured from buildRequest result so the SSE / JSON branch
    // below can read it. 'sse' is the default; features that need a single
    // JSON response (like space's askShift path) return 'json'.
    let responseType = 'sse';
    try {
      // Snapshot the messages array INCLUDING the new user message (but not the streaming placeholder)
      const messagesSnapshot = [...messages, userMsg];

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const reqConfig = await buildRequest({
        text,
        fileData,
        messagesSnapshot,
        signal: controller.signal,
        ...extraOptions,
        ...beforeSendResult,
      });

      if (!reqConfig?.url) {
        throw new Error('useChatThread: buildRequest must return { url, ... }');
      }
      responseType = reqConfig.responseType || 'sse';

      const bodyStr = typeof reqConfig.body === 'string' ? reqConfig.body : JSON.stringify(reqConfig.body || {});
      if (bodyStr.length > bodySizeLimit) {
        console.error('useChatThread: request too large:', bodyStr.length);
        setMessages(prev => prev.map(m => m.id === streamId
          ? { ...m, content: 'The file is too large to send directly. Try a smaller file or paste the text instead.', streaming: false }
          : m));
        setLoading(false);
        sendingRef.current = false;
        abortControllerRef.current = null;
        return;
      }

      response = await fetch(reqConfig.url, {
        method: reqConfig.method || 'POST',
        headers: reqConfig.headers || { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: bodyStr,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('useChatThread: HTTP error:', response.status, errText);
        // Show a more helpful error message for known issues
        let displayError = errorMessage;
        if (errText.includes('prompt is too long')) {
          const tokenMatch = errText.match(/(\d+) tokens/);
          displayError = `Prompt too long${tokenMatch ? ` (${tokenMatch[1]} tokens)` : ''}. Start a new workspace.`;
        }
        setMessages(prev => prev.map(m => m.id === streamId
          ? { ...m, content: displayError, streaming: false }
          : m));
        setLoading(false);
        sendingRef.current = false;
        abortControllerRef.current = null;
        onError?.(new Error(`HTTP ${response.status}: ${errText}`));
        return;
      }
    } catch (e) {
      // buildRequest threw or fetch threw before stream started
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== streamId));
        onAbort?.();
      } else {
        console.error('useChatThread: pre-stream error:', e);
        setMessages(prev => prev.map(m => m.id === streamId
          ? { ...m, content: errorMessage, streaming: false }
          : m));
        onError?.(e);
      }
      setLoading(false);
      sendingRef.current = false;
      abortControllerRef.current = null;
      return;
    }

    // ── Step 4: read response — SSE stream OR single JSON ──
    // When typingAnimation is enabled, tokens go into the streamStateRef
    // buffer and the rAF drainTick reveals them. The animation owns
    // finalization (setStreaming:false + onResponseDone). Send awaits
    // stream.completePromise so loading stays true until reveal completes.
    let finalText = '';
    let doneFired = false;
    let doneMetadata = null;
    try {
      if (responseType === 'json') {
        // Non-streaming endpoint (e.g. /api/space-session askShift).
        // One fetch.json(), extract finalText, finalize the bubble once.
        const data = await response.json();
        finalText = data.response || data.text || '';

        // Server-provided message id — used so polling/Realtime-based
        // layers (like space's postgres_changes subscription) dedupe with
        // the local placeholder instead of showing two copies after the
        // animation finalizes.
        const serverMsgId = data.message?.id || data.id || null;
        if (serverMsgId && serverMsgId !== streamId) {
          // CRITICAL: capture the OLD streamId in a const before any
          // reassignment. The setMessages callback below is deferred until
          // the next React commit, but JavaScript closures over `let`
          // variables read the CURRENT value at execution time. If we
          // reassign streamId before the callback runs, the .map() will
          // look for the NEW id (serverMsgId), miss the placeholder
          // (which still has the old id), and the rename silently fails.
          const oldStreamId = streamId;
          setMessages(prev => {
            // Rename the local streaming placeholder to use the server's id
            const renamed = prev.map(m => m.id === oldStreamId
              ? { ...m, id: serverMsgId }
              : m);
            // Drop any non-streaming twin with the same id — happens when
            // a polling loop or Realtime subscription fetched the server's
            // saved copy BEFORE this rename ran. The streaming version
            // (our local typing animation) wins; the polled/realtime twin
            // is dropped so React doesn't render two bubbles.
            const streamingIds = new Set();
            for (const m of renamed) if (m.streaming) streamingIds.add(m.id);
            return renamed.filter(m => !(streamingIds.has(m.id) && !m.streaming));
          });
          if (streamStateRef.current) {
            streamStateRef.current.streamId = serverMsgId;
          }
          streamId = serverMsgId;
        }

        if (typingAnimation && streamStateRef.current) {
          // Push entire JSON text into the buffer; animation reveals it.
          if (finalText) {
            const stream = streamStateRef.current;
            stream.pending = finalText;
            stream.finalText = finalText;
            stream.doneFired = true;
            stream.lastTickTime = performance.now();
            stream.frameId = requestAnimationFrame(drainTick);
          } else {
            // Empty JSON — remove placeholder, resolve completion immediately
            setMessages(prev => prev.filter(m => m.id !== streamId));
            const stream = streamStateRef.current;
            streamStateRef.current = null;
            stream.completeResolve?.();
          }
        } else if (finalText) {
          setMessages(prev => prev.map(m => m.id === streamId
            ? { ...m, content: finalText, streaming: false }
            : m));
        } else {
          // Empty JSON response — remove the placeholder rather than leaving
          // a hanging streaming bubble
          setMessages(prev => prev.filter(m => m.id !== streamId));
        }
        doneFired = true;
      } else {
        // SSE streaming path (default — /api/chat and innovate handler)
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split('\n\n');
          buf = parts.pop() || '';
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.t !== undefined) {
                if (typingAnimation && streamStateRef.current) {
                  // Push to buffer instead of direct render
                  const stream = streamStateRef.current;
                  stream.pending += data.t;
                  // Start animation if not already running
                  if (!stream.frameId && !stream.cancelled) {
                    stream.lastTickTime = performance.now();
                    stream.frameId = requestAnimationFrame(drainTick);
                  }
                } else {
                  setMessages(prev => prev.map(m => m.id === streamId
                    ? { ...m, content: m.content + data.t }
                    : m));
                }
                onTextToken?.(data.t);
              } else if (data.done) {
                doneFired = true;
                finalText = data.text || '';
                doneMetadata = data; // preserve chips, etc. from done event

                // If the server returned a saved message id, rename the
                // local streaming placeholder to match so realtime and
                // polling reconciliation dedupe against it. Without this,
                // Collaborate's askShift would echo a duplicate 'shift'
                // bubble the moment the DB INSERT fires via postgres_changes.
                // Solo doesn't need this because /api/chat doesn't return
                // an id; polling-only dedup handles it within the next tick.
                const serverMsgId = data.assistantMsgId || null;
                if (serverMsgId && serverMsgId !== streamId) {
                  const oldStreamId = streamId;
                  setMessages(prev => prev.map(m => m.id === oldStreamId
                    ? { ...m, id: serverMsgId }
                    : m));
                  if (streamStateRef.current) streamStateRef.current.streamId = serverMsgId;
                  streamId = serverMsgId;
                }

                if (typingAnimation && streamStateRef.current) {
                  // Animation will finalize when buffer drains
                  const stream = streamStateRef.current;
                  stream.doneFired = true;
                  stream.finalText = finalText;
                  stream.doneMetadata = data;
                  // Make sure animation is running so it can finalize
                  if (!stream.frameId && !stream.cancelled) {
                    stream.lastTickTime = performance.now();
                    stream.frameId = requestAnimationFrame(drainTick);
                  }
                } else {
                  setMessages(prev => prev.map(m => m.id === streamId
                    ? { ...m, content: finalText || m.content, streaming: false }
                    : m));
                }
              }
            } catch { /* skip parse errors — partial chunks land here */ }
          }
        }
      }

      // Wait for typing animation to drain (if active)
      if (typingAnimation && streamStateRef.current) {
        await streamStateRef.current.completePromise;
        // Animation already fired onResponseDone — don't double-fire
      } else {
        // Defensive: if stream ended without a done event, clear streaming flag
        if (!doneFired) {
          setMessages(prev => prev.map(m => m.id === streamId && m.streaming
            ? { ...m, streaming: false }
            : m));
        }
        // Fire onResponseDone with whatever final text we got
        onResponseDone?.(finalText, userMsg, { ...(doneMetadata || {}), assistantMsgId: streamId });
      }

    } catch (e) {
      // If typing animation is active, mark cancelled and clean up
      if (typingAnimation && streamStateRef.current) {
        const stream = streamStateRef.current;
        stream.cancelled = true;
        if (stream.frameId) cancelAnimationFrame(stream.frameId);
        streamStateRef.current = null;
        stream.completeResolve?.();
      }
      if (e.name === 'AbortError') {
        // User clicked stop mid-stream — remove the empty/partial bubble
        setMessages(prev => prev.filter(m => m.id !== streamId));
        onAbort?.();
      } else {
        console.error('useChatThread: stream error:', e);
        setMessages(prev => prev.map(m => m.id === streamId
          ? { ...m, content: errorMessage, streaming: false }
          : m));
        onError?.(e);
      }
    } finally {
      setLoading(false);
      sendingRef.current = false;
      abortControllerRef.current = null;
      // Refocus input after a tick (matches existing UX)
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [messages, setMessages, buildRequest, beforeSend, onResponseDone, onTextToken, onAbort, onError, loading, bodySizeLimit, errorMessage]);

  return {
    // Refs to attach to DOM
    scrollContainerRef,
    lastUserMsgRef,
    inputRef,
    // Feature MUST attach this to a <div> at the end of its messages list
    // for the pin/follow scroll engine to work correctly. Without it, the
    // hook falls back to the old pin-via-scrollTop approach (which needs
    // the feature to supply 100vh bottom padding).
    spacerRef,

    // State
    loading,
    showScrollBtn,
    setShowScrollBtn,

    // Actions
    send,
    abort,
    scrollToBottom,

    // Internal refs (exposed in case a feature needs to inspect/override)
    sendingRef,
    abortControllerRef,
    pinnedScrollRef,
    isLockedRef,
  };
}
